import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameState } from '../engine/state';
import { enterPhase } from '../engine/phase';
import { rotateTurn } from './turnFlow';
import type { SeatIdx } from '../types';

// The Special Building Phase (outline §13.2; plan §7.6): the classic pre-2022 5–6 seat
// rule. After each roller's turn every other live seat, clockwise, gets a window to
// BUILD and BUY advance cards — no trading of any kind, no card play, no winning (SBP
// is never win-eligible; a seat that crosses the target during SBP declares at their
// next PRE_ROLL entry). Replaces turn-flow's END_TURN (ordered override). No openWindow
// at SBP hand-offs: the 1-card allowance belongs to real turn windows only.

/** Clockwise live seats after `roller`, no wrap past the roller. */
function sbpQueue(state: GameState, roller: SeatIdx): SeatIdx[] {
  const n = state.seats.length;
  const out: SeatIdx[] = [];
  for (let step = 1; step < n; step++) {
    const s = (roller + step) % n;
    if (!state.seats[s]!.resigned) out.push(s);
  }
  return out;
}

export const specialBuildingPhaseModule: RuleModule = {
  id: 'special-building-phase',
  register(ctx) {
    ctx.phaseState('SBP', {
      eligibleWinner: () => null, // no winning during SBP (outline §13.2)
      timeoutKey: 'SBP',
    });

    ctx.action('END_TURN', {
      enumerate(state, _env, seat) {
        const p = state.phase;
        return (p.t === 'ACTION' || p.t === 'SBP') && p.seat === seat ? [{}] : null;
      },
      validate(state, action) {
        const p = state.phase;
        if (p.t !== 'ACTION' && p.t !== 'SBP') return reject('WRONG_PHASE');
        if (p.seat !== action.seat) return reject('NOT_YOUR_TURN');
        return OK;
      },
      apply(state, _action, env, emit) {
        if (state.phase.t === 'ACTION') {
          const roller = state.phase.seat;
          const queue = sbpQueue(state, roller);
          if (queue.length === 0) {
            rotateTurn(state, env, emit);
            return;
          }
          enterPhase(state, env, { t: 'SBP', roller, seat: queue[0]!, queue: queue.slice(1) }, emit);
          emit({ kind: 'WINDOW_OPENED', vis: 'public', window: 'SBP', seat: queue[0]! });
        } else if (state.phase.t === 'SBP') {
          const { roller, queue } = state.phase;
          if (queue.length === 0) {
            rotateTurn(state, env, emit); // the ring is done — next roller
            return;
          }
          enterPhase(state, env, { t: 'SBP', roller, seat: queue[0]!, queue: queue.slice(1) }, emit);
          emit({ kind: 'WINDOW_OPENED', vis: 'public', window: 'SBP', seat: queue[0]! });
        }
      },
    });
  },
};
